/**
 * Reusable "Manage clients" panel — invite a client to a brand's whitelabel
 * report portal for the GLOBAL active brand (per-client, per-brand), plus the
 * roster of that brand's existing portal clients with resend / revoke controls.
 *
 * Extracted from the former `PortalClients` reporting sub-page so the same
 * invite + magic-link flow can be surfaced on the Team page (product-owner
 * decision: client management + inviting lives on Team, gated on the existing
 * `portal.clients.manage` capability — NOT a new role). Renders bare panel
 * content (no page chrome) so the host page owns the surrounding layout. The
 * host (TeamPage) already gates rendering on `portal.clients.manage`, so the
 * roster + actions below inherit that gate.
 *
 * The backend send is best-effort (email is a P1 TODO server-side), so we
 * always surface the returned magic link with a copy button as the fallback.
 * The invite is scoped to the active brand (switched from the bottom-left
 * sidebar button); the backend re-checks the caller's access on invite (403).
 *
 * Roster endpoints (central-api Modules/ClientPortal):
 *   GET  client-portal/clients?shop_slug=      → { clients: PortalClient[] }
 *   POST client-portal/clients/{id}/resend     → { sent, magic_link_url, ... }
 *   POST client-portal/clients/{id}/revoke     → { revoked }
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import { setMessage } from '../../../store/reducers/appSlice';
import { isValidEmail } from '../../../lib/utils/email';
import type { RootState } from '../../../store/store';
import {
  invitePortalClient,
  listPortalClients,
  resendPortalClient,
  revokePortalClient,
  type InviteClientResult,
  type PortalClient,
} from '../../../lib/api/client-reports/client-reports-api';
import { englishT, type Translate } from '../agents/agent-helpers';
import { formatDateTime } from '../workbench/workbench-labels';
import '../client-reports.scss';

// The roster endpoints (central-api Modules/ClientPortal) shipped to staging
// + live in the 2026-07-23 release, so the DEV-first hold (see route.ts
// portal flags) is over.
const ROSTER_ENABLED = true;

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  invited: 'Invited',
  revoked: 'Revoked',
};

/** The roster badge text; a status this panel does not know is shown as sent. */
export const portalClientStatusLabel = (
  status: string,
  t: Translate = englishT
): string =>
  STATUS_LABEL[status]
    ? t(`PortalClientsStatus_${status}`, STATUS_LABEL[status])
    : status;

/** Last login in the app language's date and time format; a dash without one. */
const formatWhen = (iso: string | null, language?: string): string =>
  formatDateTime(iso, language) || '—';

const ManageClientsPanel = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  // Read inside the roster fetch through a ref, so a language switch never
  // refetches.
  const tRef = useRef(t);
  tRef.current = t;

  // Invite for the GLOBAL active brand (switched from the bottom-left sidebar
  // button) instead of an in-page brand selector. The backend re-checks the
  // caller's access to the brand on invite (403 handled below).
  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const allShops = useSelector(
    (s: RootState) => s.user?.profile?.all_shops || []
  );
  const activeShopName = useMemo(
    () =>
      allShops.find((sh: any) => sh?.slug === activeShopSlug)?.name ||
      activeShopSlug,
    [allShops, activeShopSlug]
  );

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Why the last invite was not created; shown under the e-mail field.
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteClientResult | null>(null);

  // Roster of the active brand's portal clients.
  const [clients, setClients] = useState<PortalClient[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [rosterError, setRosterError] = useState<string | null>(null);
  // Which row currently has an in-flight resend/revoke (keyed by client id).
  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});

  const loadClients = useCallback(async () => {
    if (!activeShopSlug) {
      setClients([]);
      setRosterError(null);
      return;
    }
    setLoadingClients(true);
    setRosterError(null);
    try {
      const rows = await listPortalClients(activeShopSlug);
      setClients(rows);
    } catch (err: any) {
      const msg =
        err?.status === 403
          ? tRef.current(
              'AgentConfigNoBrandAccess',
              'You do not have access to that brand.'
            )
          : err?.message ||
            tRef.current(
              'PortalClientsRosterLoadFailed',
              'Failed to load the client roster.'
            );
      setRosterError(msg);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, [activeShopSlug]);

  // Load (and reload on brand switch) the roster — only where the API exists.
  useEffect(() => {
    if (ROSTER_ENABLED) loadClients();
  }, [loadClients]);

  const setBusy = (id: string, busy: boolean) =>
    setRowBusy((prev) => ({ ...prev, [id]: busy }));

  const handleInvite = async () => {
    if (!activeShopSlug) {
      dispatch(
        setMessage({
          message: t(
            'AgentsNoActiveBrand',
            'No active brand. Pick one from the button in the bottom-left.'
          ),
          messageType: 'error',
        })
      );
      return;
    }
    if (!isValidEmail(email)) {
      const invalid = t(
        'PortalClientsInvalidEmail',
        'Enter a valid email address.'
      );
      setFormError(invalid);
      dispatch(setMessage({ message: invalid, messageType: 'error' }));
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await invitePortalClient({
        shop_slug: activeShopSlug,
        email: email.trim(),
        name: name.trim() || undefined,
      });
      setResult(res);
      dispatch(
        setMessage({
          message: res.email_sent
            ? t('PortalClientsInviteEmailSent', 'Invite email sent.')
            : t(
                'PortalClientsMagicLinkCreated',
                'Magic link created — copy it below to share.'
              ),
          messageType: 'success',
        })
      );
      setEmail('');
      setName('');
      // Reflect the freshly-invited client in the roster.
      loadClients();
    } catch (err: any) {
      const msg =
        err?.status === 403
          ? t(
              'AgentConfigNoBrandAccess',
              'You do not have access to that brand.'
            )
          : err?.message ||
            t('PortalClientsInviteFailed', 'Failed to create the invite.');
      setFormError(msg);
      dispatch(setMessage({ message: msg, messageType: 'error' }));
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      dispatch(
        setMessage({
          message: t('PortalClientsLinkCopied', 'Link copied.'),
          messageType: 'success',
        })
      );
    } catch {
      dispatch(
        setMessage({
          message: t(
            'PortalClientsCopyFailed',
            'Could not copy — select the link manually.'
          ),
          messageType: 'error',
        })
      );
    }
  };

  const copyLink = () => {
    if (!result?.magic_link_url) return;
    copyToClipboard(result.magic_link_url);
  };

  const handleResend = async (client: PortalClient) => {
    setBusy(client.id, true);
    try {
      const res = await resendPortalClient(client.id);
      // Mirror the invite UX: copy the fresh magic link so it can be shared
      // out-of-band even when the email send is best-effort.
      if (res.magic_link_url) {
        await copyToClipboard(res.magic_link_url);
      }
      dispatch(
        setMessage({
          message: res.email_sent
            ? t(
                'PortalClientsResent',
                'Invite re-sent to {{email}} — link also copied.',
                { email: client.email }
              )
            : t(
                'PortalClientsFreshLink',
                'Fresh link generated for {{email}} and copied to clipboard.',
                { email: client.email }
              ),
          messageType: 'success',
        })
      );
      loadClients();
    } catch (err: any) {
      const msg =
        err?.code === 'client_revoked' || err?.status === 409
          ? t(
              'PortalClientsResendRevoked',
              'This client is revoked — invite the email again to restore access.'
            )
          : err?.status === 404
          ? t('PortalClientsGone', 'That client no longer exists.')
          : err?.message ||
            t('PortalClientsResendFailed', 'Failed to resend the invite.');
      dispatch(setMessage({ message: msg, messageType: 'error' }));
    } finally {
      setBusy(client.id, false);
    }
  };

  const handleRevoke = async (client: PortalClient) => {
    const label = client.name
      ? `${client.name} (${client.email})`
      : client.email;
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        t(
          'PortalClientsRevokeConfirm',
          'Revoke portal access for {{client}}? Their magic link and any live session stop working immediately. You can re-invite the email later.',
          { client: label }
        )
      )
    ) {
      return;
    }
    setBusy(client.id, true);
    try {
      await revokePortalClient(client.id);
      dispatch(
        setMessage({
          message: t(
            'PortalClientsRevokedToast',
            'Revoked portal access for {{email}}.',
            { email: client.email }
          ),
          messageType: 'success',
        })
      );
      loadClients();
    } catch (err: any) {
      const msg =
        err?.status === 404
          ? t('PortalClientsGone', 'That client no longer exists.')
          : err?.message ||
            t('PortalClientsRevokeFailed', 'Failed to revoke the client.');
      dispatch(setMessage({ message: msg, messageType: 'error' }));
    } finally {
      setBusy(client.id, false);
    }
  };

  return (
    <div className='portal-clients'>
      <p className='portal-clients__note'>
        {t(
          'PortalClientsIntro',
          "Invite a client to a brand's whitelabel report portal. They receive a magic link (no password) that opens the agency-branded portal where approved reports appear. Invites are scoped to the active brand, so you add clients per brand."
        )}
      </p>

      <div className='portal-clients__field'>
        <label className='portal-clients__label'>
          {t('ClientReportsActiveBrand', 'Active brand')}
        </label>
        <div className='client-reports__active-brand'>
          <span className='client-reports__active-brand-name'>
            {activeShopName || '—'}
          </span>
          <span className='client-reports__active-brand-hint'>
            {t(
              'ClientReportsSwitchBrandHint',
              'Switch brand from the button in the bottom-left.'
            )}
          </span>
        </div>
      </div>

      <div className='portal-clients__field'>
        <label className='portal-clients__label' htmlFor='pc-email'>
          {t('PortalClientsEmailLabel', 'Client email')}
        </label>
        <input
          id='pc-email'
          type='email'
          className='portal-clients__input'
          placeholder={t('PortalClientsEmailPlaceholder', 'client@example.com')}
          value={email}
          aria-invalid={!!formError}
          onChange={(e) => {
            setEmail(e.target.value);
            setFormError(null);
          }}
        />
        {formError && (
          <p className='portal-clients__field-error' role='alert'>
            {formError}
          </p>
        )}
      </div>

      <div className='portal-clients__field'>
        <label className='portal-clients__label' htmlFor='pc-name'>
          {t('PortalClientsNameLabel', 'Client name (optional)')}
        </label>
        <input
          id='pc-name'
          className='portal-clients__input'
          placeholder={t('PortalClientsNamePlaceholder', 'Jane Doe')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div>
        <MainSquareButton
          type='button'
          value={t('SendInvite', 'Send invite')}
          buttonType='success'
          normal
          loading={submitting}
          disabled={submitting}
          onClick={handleInvite}
        />
      </div>

      {result && (
        <div className='portal-clients__result'>
          <p className='portal-clients__result-title'>
            {result.email_sent
              ? t('PortalClientsInviteSentTo', 'Invite sent to {{email}}', {
                  email: result.data.email,
                })
              : t('PortalClientsMagicLinkFor', 'Magic link for {{email}}', {
                  email: result.data.email,
                })}
          </p>
          <div className='portal-clients__link-row'>
            <input
              className='portal-clients__link-input'
              readOnly
              value={result.magic_link_url}
              onFocus={(e) => e.currentTarget.select()}
            />
            <MainSquareButton
              type='button'
              value={t('Copy', 'Copy')}
              buttonType='light'
              normal
              onClick={copyLink}
            />
          </div>
        </div>
      )}

      {/* Roster of the active brand's portal clients. */}
      <div className='portal-clients__roster'>
        <div className='portal-clients__roster-head'>
          <span className='portal-clients__label'>
            {activeShopName
              ? t(
                  'PortalClientsRosterTitleBrand',
                  'Portal clients — {{brand}}',
                  { brand: activeShopName }
                )
              : t('PortalClientsRosterTitle', 'Portal clients')}
          </span>
          <button
            type='button'
            className='portal-clients__refresh'
            onClick={loadClients}
            disabled={loadingClients || !activeShopSlug || !ROSTER_ENABLED}
          >
            {t('Refresh', 'Refresh')}
          </button>
        </div>

        {!ROSTER_ENABLED ? (
          <p className='portal-clients__roster-empty'>
            {t(
              'PortalClientsRosterUnavailable',
              'The client roster (list, resend, revoke) becomes available here once the report portal is enabled for this workspace.'
            )}
          </p>
        ) : loadingClients ? (
          <div className='portal-clients__roster-loading'>
            <LoadingSpinner />
          </div>
        ) : rosterError ? (
          <p className='portal-clients__roster-empty'>{rosterError}</p>
        ) : clients.length === 0 ? (
          <p className='portal-clients__roster-empty'>
            {t(
              'PortalClientsRosterEmpty',
              'No portal clients for this brand yet. Invite one above.'
            )}
          </p>
        ) : (
          <div className='portal-clients__table-scroll'>
            <table className='portal-clients__table'>
              <thead>
                <tr>
                  <th>{t('PortalClientsColClient', 'Client')}</th>
                  <th>{t('Status', 'Status')}</th>
                  <th>{t('PortalClientsColLastLogin', 'Last login')}</th>
                  <th aria-label={t('Actions', 'Actions')} />
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => {
                  const busy = !!rowBusy[client.id];
                  const revoked = client.status === 'revoked';
                  return (
                    <tr key={client.id}>
                      <td>
                        <span className='portal-clients__client-email'>
                          {client.email}
                        </span>
                        {client.name && (
                          <span className='portal-clients__client-name'>
                            {client.name}
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`portal-clients__badge portal-clients__badge--${
                            revoked
                              ? 'revoked'
                              : client.status === 'active'
                              ? 'active'
                              : 'invited'
                          }`}
                        >
                          {portalClientStatusLabel(client.status, t)}
                        </span>
                      </td>
                      <td className='portal-clients__when'>
                        {formatWhen(client.last_login_at, i18n.language)}
                      </td>
                      <td className='portal-clients__actions'>
                        <button
                          type='button'
                          className='portal-clients__rowbtn'
                          onClick={() => handleResend(client)}
                          disabled={busy || revoked}
                          title={
                            revoked
                              ? t(
                                  'PortalClientsResendTitleRevoked',
                                  'Revoked clients cannot be resent — re-invite the email'
                                )
                              : t(
                                  'PortalClientsResendTitle',
                                  'Re-send the invite and copy a fresh magic link'
                                )
                          }
                        >
                          {t('Resend', 'Resend')}
                        </button>
                        <button
                          type='button'
                          className='portal-clients__rowbtn portal-clients__rowbtn--danger'
                          onClick={() => handleRevoke(client)}
                          disabled={busy || revoked}
                          title={
                            revoked
                              ? t(
                                  'PortalClientsRevokeTitleRevoked',
                                  'Already revoked'
                                )
                              : t(
                                  'PortalClientsRevokeTitle',
                                  'Revoke this client’s portal access'
                                )
                          }
                        >
                          {t('PortalClientsRevoke', 'Revoke')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageClientsPanel;
